import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import type { StepSchema, RunState } from 'shared';
import { LineDialogView } from './LineDialogView';
import { resolveMediaRefToUrl } from '../../utils/mediaRef';

type LineDialogStep = Extract<StepSchema, { type: 'line_dialog' }>;

interface LineDialogPanelShellProps {
  step: LineDialogStep;
  state?: RunState | null;
  onChoiceSelect?: (optionIndex?: number) => void;
  onFreeInputSubmit?: (text: string) => void;
  onComplete?: () => void;
}

/**
 * P7-4「その場でスマホを開く」（presentation='panel'）のシェル。
 * 正本: docs/prod/shell_immersion_proposal_2026-07-02.md §P7-4 / モック
 * tmp/immersion_mock/index.html ③A-2（buildLinePanelDemo）。
 *
 * 設計上の要点（開閉と mount の分離）:
 * - LineDialogView は常に mount したまま保つ（display:none/unmount は絶対にしない）。
 *   LineDialogView 内部のメッセージ逐次表示タイマー・superatural effect の fired ガードは
 *   「マウントされ続けている」前提で作られており、unmount→remount するとタイマーが
 *   再スタートし、effect の fired 状態もリセットされて演出が壊れる。
 * - 開閉は CSS の transform（slide-in/out）だけで行う。閉じている間も LineDialogView は
 *   裏で普通に動き続け、メッセージは進み、超常演出も発火し、AI返信も届く。
 * - 未読バッジは LineDialogView からの onMessageRevealed（other 側メッセージの新規表示）を
 *   数える。パネルを開いたら 0 にリセットする。
 * - 「操作待ち」（choice/free input/自動送りタップ）が発生している間は、閉じていても
 *   バッジをパルスさせて呼び戻す（onWaitingForActionChange）。
 */
export function LineDialogPanelShell({
  step,
  state,
  onChoiceSelect,
  onFreeInputSubmit,
  onComplete
}: LineDialogPanelShellProps): JSX.Element {
  const [panelOpen, setPanelOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [waitingForAction, setWaitingForAction] = useState(false);
  // 着信 FAB は「最初のメッセージが届くまで」は出さない（無音の待機中に浮遊ボタンだけ
  // 出現しているのは不自然なため）。一度でも表示条件を満たしたら true のまま維持する。
  const [fabShown, setFabShown] = useState(false);

  const handleMessageRevealed = useCallback(({ side }: { side: 'other' | 'self'; totalVisible: number }) => {
    setFabShown(true);
    setPanelOpen((isOpen) => {
      if (!isOpen && side === 'other') {
        setUnreadCount((c) => c + 1);
      }
      return isOpen;
    });
  }, []);

  const handleWaitingForActionChange = useCallback((waiting: boolean) => {
    setWaitingForAction(waiting);
    if (waiting) setFabShown(true);
  }, []);

  const openPanel = useCallback(() => {
    setPanelOpen(true);
    setUnreadCount(0);
  }, []);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
  }, []);

  // Escape で閉じる（既存 LineHistoryPanel と同じ操作性）。
  useEffect(() => {
    if (!panelOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [panelOpen, closePanel]);

  // step が切り替わったら（key={step.id} で LineDialogPanelShell 自体が作り直されるため
  // 通常は不要だが）念のため初期状態に戻す。
  const stepIdRef = useRef(step.id);
  useEffect(() => {
    if (stepIdRef.current !== step.id) {
      stepIdRef.current = step.id;
      setPanelOpen(false);
      setUnreadCount(0);
      setWaitingForAction(false);
      setFabShown(false);
    }
  }, [step.id]);

  const showBadge = unreadCount > 0 || waitingForAction;
  const badgeBuzz = showBadge && !panelOpen;

  return (
    <div className="line-panel-shell" aria-hidden={false}>
      {fabShown && !panelOpen && (
        <button
          type="button"
          className={`msg-fab${badgeBuzz ? ' msg-fab--buzz' : ''}`}
          onClick={openPanel}
          aria-label="メッセージを開く"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
          {showBadge && (
            <span className={`msg-fab__badge${waitingForAction ? ' msg-fab__badge--pulse' : ''}`}>
              {unreadCount > 9 ? '9+' : unreadCount > 0 ? unreadCount : '!'}
            </span>
          )}
        </button>
      )}

      <div className={`line-panel${panelOpen ? ' line-panel--open' : ''}`}>
        <div className="line-panel__header" style={{ backgroundColor: step.theme?.headerColor ?? '#06C755' }}>
          <button type="button" className="line-panel__close" onClick={closePanel} aria-label="閉じる">
            &times;
          </button>
          {step.chatProfileImage ? (
            <img className="line-panel__header-avatar" src={resolveMediaRefToUrl(step.chatProfileImage) ?? step.chatProfileImage} alt="" />
          ) : (
            <span className="line-panel__header-avatar line-panel__header-avatar--placeholder" aria-hidden="true" />
          )}
          <span className="line-panel__header-title">{step.chatTitle}</span>
        </div>
        <div className="line-panel__body">
          <LineDialogView
            key={step.id}
            step={step}
            state={state}
            onChoiceSelect={onChoiceSelect}
            onFreeInputSubmit={onFreeInputSubmit}
            onComplete={onComplete}
            onMessageRevealed={handleMessageRevealed}
            onWaitingForActionChange={handleWaitingForActionChange}
            hideHeader
          />
        </div>
      </div>
    </div>
  );
}

export default LineDialogPanelShell;
